/**
 * Voicemails — list of boxes. Click a row to drill into the box's
 * messages on /voicemails/:id. Mirrors the FoldersList → FolderDetail
 * flow so the sidebar pattern (boxes / messages side-by-side) is gone;
 * the experience is now: pick a box, then see its messages.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { voicemails } from '@/api/voicemails';
import type { VoicemailBoxRow, CreateBoxBody } from '@/api/voicemails';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Plus, Mic, Voicemail, ChevronRight, Trash2 } from 'lucide-react';
import { useCustomerPicker } from '@/components/CustomerPicker';
import { CustomerScopeChip } from '@/components/CustomerScopeChip';

const MUTATE_ROLES = ['super_admin', 'reseller_owner', 'office_manager'];

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const now = Date.now();
  const diffMs = now - d.getTime();
  const day = 86_400_000;
  if (diffMs < day) return 'Today';
  if (diffMs < 2 * day) return 'Yesterday';
  if (diffMs < 7 * day) return `${Math.floor(diffMs / day)} days ago`;
  return d.toLocaleDateString();
}

export function VoicemailsPage() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const effectiveCustomerId = useEffectiveCustomerId();
  const canMutate = MUTATE_ROLES.includes(user.role);
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [search, setSearch] = useState('');

  const { data: boxData, isLoading } = useQuery({
    queryKey: ['voicemailBoxes', effectiveCustomerId],
    queryFn: () => voicemails.listBoxes({ customer_id: effectiveCustomerId ?? undefined }),
  });
  const allBoxes = boxData?.rows ?? [];

  const boxes = search.trim()
    ? allBoxes.filter((b) => b.name.toLowerCase().includes(search.trim().toLowerCase()))
    : allBoxes;

  return (
    <>
      <PageHeader
        eyebrow={t('navGroups.telephony')}
        title={t('voicemail.title')}
        description={t('voicemail.description')}
        actions={canMutate && (
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" />
            {t('voicemail.newBox')}
          </Button>
        )}
      />

      <CustomerScopeChip />

      {/* Search bar — only render when there are enough boxes to filter */}
      {!isLoading && allBoxes.length > 0 && (
        <div className="mb-4">
          <Input
            placeholder="Search boxes…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-sm"
          />
        </div>
      )}

      {isLoading ? (
        <div className="text-sm text-slate-400">{t('common.loading')}</div>
      ) : allBoxes.length === 0 ? (
        <EmptyState onCreate={canMutate ? () => setCreateOpen(true) : undefined} />
      ) : boxes.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No voicemail boxes match &ldquo;{search}&rdquo;.
          </CardContent>
        </Card>
      ) : (
        <BoxesTable
          boxes={boxes}
          canMutate={canMutate}
          onOpen={(b) => navigate(`/voicemails/VMB-${b.ref_number}`)}
        />
      )}

      <CreateBoxDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(b) => navigate(`/voicemails/VMB-${b.ref_number}`)}
      />
    </>
  );
}

// ── Empty state ────────────────────────────────────────────────────────

function EmptyState({ onCreate }: { onCreate?: () => void }) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent className="py-12 text-center">
        <Mic className="mx-auto h-8 w-8 text-slate-300 mb-3" />
        <p className="text-sm font-medium text-slate-500">{t('voicemail.noBoxesYet')}</p>
        <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
          {t('voicemail.noBoxesDescription')}
        </p>
        {onCreate && (
          <Button size="sm" className="mt-4" onClick={onCreate}>
            <Plus className="h-4 w-4 mr-1.5" />
            {t('voicemail.createFirstBox')}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// ── Boxes table ────────────────────────────────────────────────────────
//
// Single-bordered Card (overflow-hidden) with row dividers — same shape
// as FoldersList.FolderTable. No nested rounded box around the rows.

function BoxesTable({
  boxes, canMutate, onOpen,
}: {
  boxes: VoicemailBoxRow[];
  canMutate: boolean;
  onOpen: (b: VoicemailBoxRow) => void;
}) {
  const qc = useQueryClient();
  const effectiveCustomerId = useEffectiveCustomerId();

  const remove = useMutation({
    mutationFn: (id: string) => voicemails.deleteBox(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['voicemailBoxes', effectiveCustomerId] }),
  });

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        {/* Column headers */}
        <div className="grid grid-cols-[1fr_8rem_9rem_auto] gap-0 border-b border-border bg-slate-50/60 px-4 py-2">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Name</div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Owner type</div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Created</div>
          <div />
        </div>
        <ul className="divide-y divide-border">
          {boxes.map((b) => (
            <li key={b.id}>
              <div className="grid grid-cols-[1fr_8rem_9rem_auto] gap-0 items-center px-4 py-3 text-sm transition-colors hover:bg-slate-50/60 group">
                {/* Name + click-to-open */}
                <button
                  type="button"
                  onClick={() => onOpen(b)}
                  className="flex items-center gap-2 text-left min-w-0"
                >
                  <Voicemail className="h-4 w-4 shrink-0 text-amber-600 group-hover:text-amber-700 transition-colors" />
                  <div className="min-w-0">
                    <div className="font-medium truncate group-hover:text-primary transition-colors">
                      {b.name}
                    </div>
                  </div>
                  <ChevronRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-primary opacity-0 group-hover:opacity-100 transition-all shrink-0" />
                </button>

                {/* Owner kind chip */}
                <div className="text-xs">
                  <span className="inline-flex items-center gap-1 rounded bg-amber-50 border border-amber-200 px-1.5 py-0.5 text-amber-700 capitalize">
                    {b.owner_kind.replace('_', ' ')}
                  </span>
                </div>

                {/* Created date */}
                <div className="text-xs text-muted-foreground">
                  {formatDate(b.created_at)}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1">
                  {canMutate && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0"
                      disabled={remove.isPending}
                      onClick={() => {
                        if (window.confirm(`Delete "${b.name}"? Messages inside will be permanently deleted.`)) {
                          remove.mutate(b.id);
                        }
                      }}
                      aria-label="Delete box"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-red-500" />
                    </Button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

// ── Create box dialog ──────────────────────────────────────────────────
//
// Same shape as before; just emits the new row to onCreated so the
// caller can navigate straight to the new box.

function CreateBoxDialog({
  open, onOpenChange, onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: (box: VoicemailBoxRow) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [ownerKind, setOwnerKind] = useState<VoicemailBoxRow['owner_kind']>('account');
  const [notifyEmail, setNotifyEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const { effectiveCustomerId, element: customerPicker, isPickerVisible } =
    useCustomerPicker(customerId, setCustomerId, { required: true });
  void isPickerVisible;

  const create = useMutation({
    mutationFn: (body: CreateBoxBody) => voicemails.createBox(body),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['voicemailBoxes'] });
      setName(''); setNotifyEmail(''); setError(null); setCustomerId(null);
      onOpenChange(false);
      onCreated(row);
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : 'Create failed.');
    },
  });

  const canSubmit = name.trim().length > 0 && effectiveCustomerId !== null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('voicemail.newBoxTitle')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {customerPicker}
          <CustomerScopeChip />
          <div className="space-y-1">
            <Label htmlFor="vm-name">{t('voicemail.boxName')}</Label>
            <Input id="vm-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('voicemail.namePlaceholder')} />
          </div>
          <div className="space-y-1">
            <Label>{t('voicemail.ownerType')}</Label>
            <Select value={ownerKind} onValueChange={(v) => setOwnerKind(v as VoicemailBoxRow['owner_kind'])}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="account">{t('voicemail.ownerTypeAccountWide')}</SelectItem>
                <SelectItem value="ivr_node">{t('voicemail.ownerTypeIvrNode')}</SelectItem>
                <SelectItem value="extension">{t('voicemail.ownerTypeExtension')}</SelectItem>
                <SelectItem value="caller">{t('voicemail.ownerTypeCaller')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="vm-notify">{t('voicemail.notifyEmail')}</Label>
            <Input id="vm-notify" type="email" value={notifyEmail} onChange={(e) => setNotifyEmail(e.target.value)} />
            <p className="text-xs text-muted-foreground">{t('voicemail.notifyEmailHint')}</p>
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={create.isPending}>{t('common.cancel')}</Button>
          </DialogClose>
          <Button
            type="button"
            disabled={create.isPending || !canSubmit}
            onClick={() => create.mutate({
              name: name.trim(),
              owner_kind: ownerKind,
              notify_email: notifyEmail || null,
              ...(effectiveCustomerId
                ? { customer_id: effectiveCustomerId }
                : {}),
            })}
          >
            {create.isPending ? t('voicemail.creating') : t('voicemail.createBox')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
